import { FieldFilter } from '@6grain/masofa-monolith-api-client/common/model/fieldFilter';
import { FileContentType } from '@6grain/masofa-monolith-api-client/common/model/fileContentType';
import { FileStorageItem } from '@6grain/masofa-monolith-api-client/common/model/fileStorageItem';
import { FileStorageItemBaseGetQuery } from '@6grain/masofa-monolith-api-client/common/model/fileStorageItemBaseGetQuery';
import { FilterOperator } from '@6grain/masofa-monolith-api-client/common/model/filterOperator';
import { SortType } from '@6grain/masofa-monolith-api-client/common/model/sortType';
import { StatusType } from '@6grain/masofa-monolith-api-client/common/model/statusType';
import { formatDatabaseSizeBytes } from '@app/pages/admin/orchestration-database-connections.util';
import { fileStorageDownloadUrl } from '@app/pages/admin/orchestration-object-storage.util';

export const BUCKET_FILES_PAGE_SIZE = 100;
export const BUCKET_FILES_PAGE_SIZES = [10, 25, 50, 100] as const;
export const BUCKET_FILES_SEARCH_DEBOUNCE_MS = 300;
/** Fit inside the preview dialog. The download link stays the full object. */
export const BUCKET_FILE_PREVIEW_WIDTH = 1600;

const PREVIEWABLE = new Set<string>([
  String(FileContentType.NUMBER_1),
  String(FileContentType.NUMBER_2),
  String(FileContentType.NUMBER_3),
  'imagejpg',
  'imagepng',
  'imagewebp',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

export interface BucketFileRow {
  id: string;
  path: string;
  fileName: string;
  sizeLabel: string;
  contentType: string;
  createdAt: string | null;
  downloadUrl: string;
  previewUrl: string | null;
}

export function buildBucketFilesQuery(input: {
  bucket: string;
  search: string;
  take: number;
  offset: number;
}): FileStorageItemBaseGetQuery {
  const filters: FieldFilter[] = [
    {
      filterField: 'fileStorageBacket',
      filterValue: input.bucket,
      filterOperator: FilterOperator.NUMBER_0,
    },
    {
      filterField: 'status',
      filterValue: StatusType.NUMBER_1,
      filterOperator: FilterOperator.NUMBER_0,
    },
  ];
  const search = input.search.trim();
  if (search) {
    filters.push({
      filterField: 'fileStoragePath',
      filterValue: search,
      filterOperator: FilterOperator.NUMBER_6,
    });
  }
  return {
    take: input.take,
    offset: input.offset,
    sortBy: 'createAt',
    sort: SortType.NUMBER_1,
    filters,
  };
}

export function isPreviewableFile(item: {
  fileContentType?: FileContentType | string | number | null;
  fileContentTypeName?: string | null;
}): boolean {
  const type = String(item.fileContentType ?? '').trim().toLowerCase();
  const name = (item.fileContentTypeName ?? '').trim().toLowerCase();
  return PREVIEWABLE.has(type) || PREVIEWABLE.has(name);
}

export function fileNameFromStoragePath(path: string | null | undefined): string {
  const value = (path ?? '').trim();
  if (!value) {
    return '';
  }
  const parts = value.split(/[/\\]/).filter(part => part.length > 0);
  return parts.at(-1) ?? value;
}

export function toBucketFileRow(item: FileStorageItem): BucketFileRow | null {
  const id = (item.id ?? '').trim();
  const downloadUrl = fileStorageDownloadUrl(id);
  if (!id || !downloadUrl) {
    return null;
  }
  const path = (item.fileStoragePath ?? '').trim();
  const fileName = fileNameFromStoragePath(path) || id;
  const previewable = isPreviewableFile(item);
  return {
    id,
    path: path || id,
    fileName,
    sizeLabel: formatDatabaseSizeBytes(item.fileLength),
    contentType: (item.fileContentTypeName ?? '').trim() || '—',
    createdAt: item.createAt ?? null,
    downloadUrl,
    previewUrl: previewable ? `${downloadUrl}?width=${BUCKET_FILE_PREVIEW_WIDTH}` : null,
  };
}
